import { Link } from "react-router-dom";
import { Briefcase, Receipt, Database, Users, Shield, TrendingUp, ArrowRight, Calendar, Download, Mail, Phone, ExternalLink } from "lucide-react";
import { SITE } from "@/lib/site";
import Hero from "@/components/Hero";
import useReveal from "@/lib/useReveal";

const ICONS = { Briefcase, Receipt, Database, Users, Shield, TrendingUp };

export default function Home() {
  useReveal();
  return (
    <main data-testid="page-home">
      <Hero />

      {/* Services */}
      <section className="py-20 md:py-28 bg-cream-100/40 border-y border-navy-100" data-testid="home-services">
        <div className="container-narrow">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 reveal">
            <div className="max-w-2xl">
              <div className="text-[12px] tracking-[0.24em] uppercase text-gold-500 font-medium">What we do</div>
              <h2 className="font-serif-display text-3xl md:text-5xl text-navy-800 mt-3 leading-[1.06]">Holistic Approach to Professional Financial Services.</h2>
            </div>
            <Link to="/services" data-testid="home-services-all" className="btn-secondary self-start md:self-auto">Related Services <ArrowRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {SITE.services.map((s) => {
              const Icon = ICONS[s.icon] || Briefcase;
              const isExt = !!s.external;
              const commonInner = (
                <div className="flex items-start gap-4">
                  <div className="text-gold-500 font-serif-display text-2xl tabular-nums w-8 shrink-0">{s.number}</div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-3 text-navy-700">
                      <div className="flex items-center gap-3"><Icon size={18} /><span className="text-[12px] tracking-[0.2em] uppercase">{s.title.split(" ")[0]}</span></div>
                      {isExt && <ExternalLink size={14} className="text-gold-500" />}
                    </div>
                    <h3 className="font-serif-display text-[22px] md:text-[24px] text-navy-800 font-semibold mt-2 leading-tight">{s.title}</h3>
                    <p className="mt-3 text-navy-600 text-[15px] leading-relaxed">{s.desc}</p>
                    <div className="mt-5 inline-flex items-center gap-2 text-navy-700 text-sm font-medium">{isExt ? "Visit site" : "Learn more"} <ArrowRight size={15} /></div>
                  </div>
                </div>
              );
              const testid = `home-service-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`;
              return isExt ? (
                <a key={s.title} href={s.external} target="_blank" rel="noopener noreferrer" data-testid={testid} className="reveal card-lift block rounded border border-navy-100 bg-white p-7 md:p-8">
                  {commonInner}
                </a>
              ) : (
                <Link key={s.title} to="/services" data-testid={testid} className="reveal card-lift block rounded border border-navy-100 bg-white p-7 md:p-8">
                  {commonInner}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* David / team intro */}
      <section className="py-16 md:py-24" data-testid="home-team-intro">
        <div className="container-narrow grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 reveal">
            <div className="relative rounded overflow-hidden border border-navy-100 shadow-[0_30px_80px_-40px_rgba(10,37,64,0.3)]">
              <img src={SITE.team.principle.photo} alt="David St. John, Principal Advisor" className="w-full h-full object-cover" />
              <div className="absolute bottom-0 inset-x-0 p-5 bg-gradient-to-t from-black/55 via-black/20 to-transparent">
                <div className="text-white font-serif-display text-xl">David St. John</div>
                <div className="text-cream-100 text-[13px]">MBA, EA, MS<sup className="text-[9px]">Acct/Tax</sup>, CFP<sup className="text-[8px]">®</sup></div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 reveal">
            <div className="text-[12px] tracking-[0.24em] uppercase text-gold-500 font-medium">Meet the Team</div>
            <h2 className="font-serif-display text-3xl md:text-5xl text-navy-800 mt-3 leading-[1.06]">20+ years of experience. A small-town heart.</h2>
            <div className="mt-6 space-y-4 text-navy-600 text-[16px] leading-relaxed">
              <p>{SITE.team.bio[0]}</p>
              <p>{SITE.team.bio[1]}</p>
              <p>{SITE.team.bio[2]}</p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/team" data-testid="home-team-cta" className="btn-primary">Meet the Team <ArrowRight size={16} /></Link>
              <a href={SITE.team.principle.resume} target="_blank" rel="noopener noreferrer" data-testid="home-team-resume" className="btn-secondary"><Download size={16} /> David's Resume</a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-20 md:py-28" data-testid="home-trust">
        <div className="container-narrow grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 reveal">
            <div className="text-[12px] tracking-[0.24em] uppercase text-gold-500 font-medium">Why our clients trust us</div>
            <h2 className="font-serif-display text-3xl md:text-5xl text-navy-800 mt-3 leading-[1.08]">Steady, professional, personal.</h2>
            <p className="mt-5 text-navy-600 leading-relaxed text-[16px] max-w-md">Our recommendations are tailored to you and guided by the highest ethical standards.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={SITE.bookingUrl} target="_blank" rel="noopener noreferrer" data-testid="home-trust-book" className="btn-primary"><Calendar size={16} /> Free consultation</a>
              <a href={SITE.files.accountingChecklist} target="_blank" rel="noopener noreferrer" data-testid="home-trust-checklist" className="btn-secondary"><Download size={16} /> Services Checklist</a>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 items-center">
              <a href={SITE.trust.chamber} target="_blank" rel="noopener noreferrer" data-testid="home-trust-chamber"><img src={SITE.trust.chamberLogo} alt="Owasso Chamber of Commerce member" className="h-14 w-auto bg-white rounded p-1.5 border border-navy-100" /></a>
              <a href={SITE.trust.bbb} target="_blank" rel="noopener noreferrer" data-testid="home-trust-bbb"><img src={SITE.trust.bbbLogo} alt="BBB Accredited" className="h-14 w-auto bg-white rounded p-1.5 border border-navy-100" /></a>
              <img src={SITE.trust.proadvisorBadge} alt="Intuit QuickBooks ProAdvisor, Platinum" className="h-14 w-auto" />
            </div>
          </div>
          <div className="lg:col-span-7 reveal">
            <ul className="columns-1 sm:columns-2 gap-x-8">
              {SITE.benefits.map((b, i) => (
                <li key={i} className="flex gap-3 items-start break-inside-avoid mb-4">
                  <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-gold-500 shrink-0" />
                  <span className="text-navy-700 text-[14.5px] leading-snug">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-cream-100/40 border-t border-navy-100" data-testid="home-cta">
        <div className="container-narrow">
          <div className="rounded border border-navy-100 bg-white p-10 md:p-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-center reveal">
            <div className="md:col-span-7">
              <div className="text-[12px] tracking-[0.24em] uppercase text-gold-500 font-medium">Ready to talk?</div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-navy-800 mt-3 leading-[1.1]">Let's build a clear financial picture together.</h2>
              <p className="mt-4 text-navy-600 text-[16px] leading-relaxed max-w-xl">Whether you're a small business or a family planning ahead, we're here to listen and help. Reach out and we'll respond within 48 business hours.</p>
            </div>
            <div className="md:col-span-5 flex flex-col gap-3">
              <Link to="/contact" data-testid="home-cta-contact" className="btn-primary"><Mail size={16} /> Contact Us</Link>
              <a href={SITE.bookingUrl} target="_blank" rel="noopener noreferrer" data-testid="home-cta-book" className="btn-secondary"><Calendar size={16} /> Book a Consultation</a>
              <a href={SITE.phoneTel} data-testid="home-cta-call" className="btn-secondary"><Phone size={16} /> Call {SITE.phone}</a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
